// Import hooks.
import {
	useInputForm,
	UseInputFormResult,
} from '@app/hooks/view/input';
// Import app models.
import {
	ValidatorResponse,
} from '@app/models/app';
// Import enumerations
import {
	BroadLineOfBusiness,
	BuildingFoundationType,
} from '@app/enumerations';
// Import api models.
import {
	PossibleFoundationType,
} from '@app/models/api';
// Import props.
import {
	InputFormProps,
	InputFoundationTypeSelectionListProps,
} from '@app/props';

interface UseInputFoundationTypeSelectionListParameters {
	id: string;
	label: string;
	required: boolean;
	disabled: boolean;
	value?: BuildingFoundationType;
	possibleFoundationTypes: PossibleFoundationType[];
	broadLineOfBusinessCode: BroadLineOfBusiness;
	showValidation?: boolean;
}

interface UseInputFoundationTypeSelectionListResults extends UseInputFormResult<BuildingFoundationType | undefined, InputFoundationTypeSelectionListProps> {
	getMatchingPossibleFoundationType: (foundationType: BuildingFoundationType | undefined) => PossibleFoundationType | undefined;
}

const useInputFoundationTypeSelectionList = (params: UseInputFoundationTypeSelectionListParameters): UseInputFoundationTypeSelectionListResults => {

	// #region exported methods

	/**
	 * Returns props for form input. 
	 **/
	const applyInputFoundationTypeSelectionListProps = (): InputFoundationTypeSelectionListProps => {
		// First apply the form input props.
		let formProps: InputFormProps = applyProps();
		// Then apply the perils selection list props.
		return {
			...formProps,
			broadLineOfBusinessCode: params.broadLineOfBusinessCode,
			selectedFoundationType: other.value,
			possibleFoundationTypes: params.possibleFoundationTypes,
			onSelectFoundationType: other.setInput,
		}
	};

	/**
	 * Attempts to find a matching possible foundation type record.
	 * @param foundationType
	 */
	const getMatchingPossibleFoundationType = (foundationType: BuildingFoundationType | undefined): PossibleFoundationType | undefined => {
		let matchingPossibleFoundationType: PossibleFoundationType | undefined = undefined;
		if (foundationType !== undefined) {
			// Find the matching possible foundation type.
			matchingPossibleFoundationType = params.possibleFoundationTypes.find((possibleFoundationType: PossibleFoundationType) => {
				return possibleFoundationType.code !== null && possibleFoundationType.code === foundationType;
			});
		}
		return matchingPossibleFoundationType;
	}

	// #endregion exported methods


	// #region private methods

	/**
	 * Checks to see if the foundation type is valid.
	 * @param foundationType
	 */
	const isFoundationTypeValidByFoundationType = (foundationType: BuildingFoundationType | undefined): boolean => {
		// Make sure that the selected foundation type is a valid option.
		return getMatchingPossibleFoundationType(foundationType) !== undefined;
	}

	/**
	 * Validates the selected foundation type.
	 * @param selectedValue
	 */
	const validateFoundationType = (selectedValue: BuildingFoundationType | undefined, required: boolean): ValidatorResponse => {
		let response: ValidatorResponse = new ValidatorResponse(true, String.empty());

		if (required
			&& selectedValue === undefined) {
			// Make sure the foundation type is not empty.
			response = new ValidatorResponse(false, 'Please select the foundation type.');
		}
		else if (selectedValue !== undefined
			&& !isFoundationTypeValidByFoundationType(selectedValue)) {
			// Make sure that any selected foundation type is a valid selection.
			response = new ValidatorResponse(false, 'Invalid selection');
		}

		return response;
	}

	/**
	 * Sets up the intial value of the input.
	 * @param initialValue
	 */
	const setupInitialValue = (initialValue: BuildingFoundationType | undefined): BuildingFoundationType | undefined => {
		let updatedInitialValue: BuildingFoundationType | undefined = initialValue;
		if (updatedInitialValue !== undefined
			&& !isFoundationTypeValidByFoundationType(updatedInitialValue)) {
			updatedInitialValue = undefined;
		}

		return updatedInitialValue;
	}

	// #endregion private methods


	// #region variables

	// Instantiate the 'data selection list' input.
	const { applyProps, ...other } = useInputForm<BuildingFoundationType | undefined>({
		id: params.id,
		label: params.label,
		required: params.required,
		disabled: params.disabled,
		initialInput: setupInitialValue(params.value),
		showValidation: params.showValidation,
		validators: [validateFoundationType],
	});

	// #endregion variables


	return {
		applyProps: applyInputFoundationTypeSelectionListProps,
		getMatchingPossibleFoundationType: getMatchingPossibleFoundationType,
		...other,
	};
}

// Export parameters.
export {
	UseInputFoundationTypeSelectionListParameters,
}

// Export results.
export {
	UseInputFoundationTypeSelectionListResults,
}

export default useInputFoundationTypeSelectionList;